@if (isProfileEdit) {
  <section class="user-editor-panel">
    <header class="user-editor-panel__header">
      <span class="user-editor-panel__eyebrow">Account &amp; profile</span>
      <h2>Edit profile</h2>
      <p>Keep your personal information and profile picture up to date.</p>
    </header>

    <form id="profile-edit-form" class="user-editor-panel__body" [formGroup]="editUserForm"
      (ngSubmit)="onFormSubmit()">
      <section class="profile-identity">
        <div class="profile-identity__avatar">
          <app-avatar [name]="currentUser.full_name" [isProfileEdit]="true"
            [imageUrl]="currentUser.profile_image" (imageSelected)="onAvatarImageSelected($event)"
            (imageDeleted)="onAvatarImageDeleted()"></app-avatar>
        </div>
        <div class="profile-identity__copy">
          <strong>{{ editUserForm.get('full_name')?.value || currentUser.full_name || 'Your profile' }}</strong>
          <span>{{ currentUser.email || 'Account email' }}</span>
          <small>Hover over the photo to change or remove it.</small>
        </div>
      </section>

      <section class="profile-section">
        <div class="profile-section__heading">
          <mat-icon>manage_accounts</mat-icon>
          <div>
            <h3>Personal details</h3>
            <p>Your email address and assigned role are managed by your workspace.</p>
          </div>
        </div>

        <mat-form-field appearance="outline">
          <mat-label>Full name</mat-label>
          <mat-icon matPrefix>person</mat-icon>
          <input matInput formControlName="full_name" #nameInput [attr.maxLength]="limits.name"
            autocomplete="name">
          @if (editUserForm.get('full_name')?.hasError('required')) {
            <mat-error>Name is required</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Email</mat-label>
          <mat-icon matPrefix>mail</mat-icon>
          <input matInput formControlName="email" [attr.maxLength]="limits.email" autocomplete="email">
          @if (editUserForm.get('email')?.hasError('required')) {
            <mat-error>Email is required</mat-error>
          }
          @if (editUserForm.get('email')?.hasError('email') || editUserForm.get('email')?.hasError('pattern')) {
            <mat-error>Please enter a valid email address</mat-error>
          }
        </mat-form-field>

        @if (!currentUser.is_admin) {
          <mat-form-field appearance="outline">
            <mat-label>Role</mat-label>
            <mat-icon matPrefix>badge</mat-icon>
            <mat-select formControlName="role" panelClass="profile-role-panel">
              @for (role of roles; track role) {
                <mat-option [value]="role.id">{{ role.name }}</mat-option>
              }
            </mat-select>
            @if (editUserForm.get('role')?.hasError('required')) {
              <mat-error>Role is required</mat-error>
            }
          </mat-form-field>
        }

        @if (currentUser.is_admin) {
          <mat-form-field appearance="outline">
            <mat-label>Role</mat-label>
            <mat-icon matPrefix>admin_panel_settings</mat-icon>
            <input matInput formControlName="role_name">
          </mat-form-field>
        }
      </section>

      @if (!currentUser.is_admin) {
        <section class="profile-danger">
          <div>
            <strong>Delete account</strong>
            <span>Permanently remove your profile and access.</span>
          </div>
          <button mat-button type="button" (click)="deleteUserProfile()">Delete account</button>
        </section>
      }
    </form>

    <footer class="user-editor-panel__footer">
      <button class="secondary-btn" mat-flat-button type="button" (click)="cancelClick()">Cancel</button>
      <button class="primary-btn" mat-flat-button type="submit" form="profile-edit-form"
        [disabled]="commonService.isLoading">
        <mat-icon>save</mat-icon>Save changes
      </button>
    </footer>
  </section>
} @else {
  <section class="user-editor-panel">
    <header class="user-editor-panel__header">
      <span class="user-editor-panel__eyebrow">Team management</span>
      <h2>{{ isUpdateUser ? 'Edit user' : 'Add user' }}</h2>
      <p>{{ isUpdateUser ? 'Update this team member’s details and workspace access.' : 'Add a team member and assign the right level of workspace access.' }}</p>
    </header>

    <form id="user-editor-form" class="user-editor-panel__body" [formGroup]="editUserForm"
      (ngSubmit)="onFormSubmit()">
      <section class="user-summary">
        <div class="user-summary__avatar">
          <app-avatar [name]="editUserForm.get('full_name')?.value || currentUser.full_name"
            [isProfileEdit]="true" [imageUrl]="currentUser.profile_image"
            (imageSelected)="onAvatarImageSelected($event)"
            (imageDeleted)="onAvatarImageDeleted()"></app-avatar>
        </div>
        <div class="user-summary__copy">
          <span>{{ isUpdateUser ? 'Team member' : 'New team member' }}</span>
          <strong>{{ editUserForm.get('full_name')?.value || 'Add a profile name' }}</strong>
          <small>{{ editUserForm.get('email')?.value || 'Email address will identify this account' }}</small>
        </div>
      </section>

      <section class="user-access-section">
        <div class="user-access-section__heading">
          <mat-icon>{{ isUpdateUser ? 'manage_accounts' : 'person_add' }}</mat-icon>
          <div>
            <h3>User details</h3>
            <p>Enter the account information and choose an appropriate role.</p>
          </div>
        </div>

        <mat-form-field appearance="outline">
          <mat-label>Full name</mat-label>
          <mat-icon matPrefix>person</mat-icon>
          <input matInput formControlName="full_name" #nameInput [attr.maxLength]="limits.name"
            autocomplete="name">
          @if (editUserForm.get('full_name')?.hasError('required')) {
            <mat-error>Name is required</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Email</mat-label>
          <mat-icon matPrefix>mail</mat-icon>
          <input matInput formControlName="email" [attr.maxLength]="limits.email" autocomplete="email">
          @if (editUserForm.get('email')?.hasError('required')) {
            <mat-error>Email is required</mat-error>
          }
          @if (editUserForm.get('email')?.hasError('email') || editUserForm.get('email')?.hasError('pattern')) {
            <mat-error>Please enter a valid email address</mat-error>
          }
        </mat-form-field>

        @if (!currentUser.is_admin) {
          <mat-form-field appearance="outline">
            <mat-label>Role</mat-label>
            <mat-icon matPrefix>badge</mat-icon>
            <mat-select formControlName="role" panelClass="user-role-panel">
              @for (role of roles; track role) {
                <mat-option [value]="role.id">{{ role.name }}</mat-option>
              }
            </mat-select>
            @if (editUserForm.get('role')?.hasError('required')) {
              <mat-error>Role is required</mat-error>
            }
          </mat-form-field>
        }

        @if (currentUser.is_admin) {
          <mat-form-field appearance="outline">
            <mat-label>Role</mat-label>
            <mat-icon matPrefix>admin_panel_settings</mat-icon>
            <input matInput formControlName="role_name">
          </mat-form-field>
        }

        <div class="user-access-note">
          <mat-icon>info</mat-icon>
          <span>Role permissions determine which areas this user can view and manage.</span>
        </div>
      </section>
    </form>

    <footer class="user-editor-panel__footer">
      <button class="secondary-btn" mat-flat-button type="button" (click)="cancelClick()">Cancel</button>
      <button class="primary-btn" mat-flat-button type="submit" form="user-editor-form"
        [disabled]="commonService.isLoading">
        <mat-icon>{{ isUpdateUser ? 'save' : 'person_add' }}</mat-icon>
        {{ isUpdateUser ? 'Save changes' : 'Add user' }}
      </button>
    </footer>
  </section>
}
